import { Location } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { PortalNavigationService } from '@pershing/nxi-ng-common';
import { AuthenticationService, ContextService, LogService, PermissionCheckerService, PershingHttpUtil, ServiceRouter, StorageService } from '@pershing/pershing-core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { catchError, filter, map, pairwise, retry, takeUntil } from 'rxjs/operators';
import { PortalConstants } from '../utils/portal.constants';
import { NavService } from './nav.service';
import { PortalComponentServiceDefinition } from './portal-component-service-definition';

@Injectable()
export class AuthService {
    menuConfig$: BehaviorSubject<any> = new BehaviorSubject([]);
    menuConfigObservable$ = this.menuConfig$.asObservable();
    private menuItems: any[] = [];
    private validSession = false;
    private landingRoute: string;
    private isVendorSso: string;
    private ibd: string;
    private deviceType: string;
    private isLandingRedirect: boolean = false;
    private serviceUrl: string;
    private nxiUri: string = '/nxi';
    private isWhiteListUrl: boolean;
    private currentRouteString: String;
    configMenu = new Map();
    private XFSTargetUrlList: any;
    private isWhiteList: boolean;
    private xfsOption: string;
    private keepAlive: string;
    private keepAliveType: string;
    private cbolObj: any;
    private userLoggedIn: boolean = false;
    unsubscribe: Subject<void> = new Subject<void>();
    qpGlobal: any;

    constructor(
        private http: HttpClient,
        private authService: AuthenticationService,
        private storageService: StorageService,
        private logService: LogService,
        private authorizationService: PermissionCheckerService,
        private contextService: ContextService,
        private navigationService: PortalNavigationService,
        private router: Router,
        private serviceRouter: ServiceRouter,
        private route: ActivatedRoute,
        private navService: NavService,
        private location: Location, @Optional() @Inject('AppSettings') appSettings: any) {
        this.serviceUrl = this.serviceRouter.getServiceRouteURL(PortalComponentServiceDefinition.getInstance());
        if (appSettings.landingPage) {
            this.landingRoute = appSettings.landingPage;
        }
        this.ibd = appSettings.ibdConfigMap.IBD;
        this.deviceType = appSettings.deviceType;
        if (appSettings.keepAlive) {
            this.keepAlive = appSettings.keepAlive;
        }
        if (appSettings.keepAliveType) {
            this.keepAliveType = appSettings.keepAliveType;
        }
        if (appSettings.dasfResponse) {
            sessionStorage.setItem('Response', appSettings.dasfResponse);
            sessionStorage.setItem('userid', JSON.parse(appSettings.dasfResponse).userid);
            sessionStorage.setItem('corrnum', appSettings.ibdConfigMap.IBD);
            sessionStorage.setItem('displayRememberDevice', 'true');
        }
        if (appSettings.isLoggedIn) {
            this.userLoggedIn = appSettings.isLoggedIn;
        }
        this.isVendorSso = appSettings.isVendorSso;
        this.cbolObj = appSettings.cbolObj || {};

        this.checkRouterEvents();
        // this.getMenuConfig().subscribe( data => { console.log('Fetched Menu Config Json');});
        this.XFSTargetUrlList = !!appSettings && !!appSettings.ibdConfigMap ? appSettings.ibdConfigMap.XFSUrlenabled : [];
        this.isWhiteList = !!appSettings && !!appSettings.isxfsWhiteList ? appSettings.isxfsWhiteList : false;
        this.xfsOption = !!appSettings && !!appSettings.ibdConfigMap ? appSettings.ibdConfigMap.xfsSelection : '';

        this.navService.menuItems$.pipe(takeUntil(this.unsubscribe)).subscribe(menus => {
            if (menus) {
              this.menuItems = menus as any[];
              console.log('>>>>MenuItems in auth service >>> ', this.menuItems);
              this.iterateMenu(this.menuItems, '/nxi', ''); // Update menuConfig map based on menu
            }
          });

    }

    checkRouterEvents() {
        this.router.events.pipe(
            filter(event => event instanceof NavigationStart) // take only when the navigation start, this is because we need the currentRouteString for route guard even before the navigation ends successfully.
        ).subscribe((event: any) => {
            if (event.url) {
                this.currentRouteString = event.url;
            }
        });
        this.router.events.pipe(
            filter(event => event instanceof NavigationStart), // take only when the navigation start
            filter(event => !(event as NavigationStart).url.includes('dummy/refresh')), // ignore dummy/refresh
            pairwise() // to get the previously emitted value; index 0 will the previous one, index 1 will be the current one
        ).subscribe((pairNavigationStartEvent: any) => {
            if (pairNavigationStartEvent[0] && pairNavigationStartEvent[0].url) {
                this.setLastUrlBeforeDisc(pairNavigationStartEvent[0]);
            }
        });
    }
    /**
     * example:
     * overview  ->  balance  -> disclosure
     * previous      current     future
     * 
     * current one is stored in lastUrlBeforeDisc, so when we navigate to disclosure, we can come back
     * @param previewNavigationStartEvent 
     */
    setLastUrlBeforeDisc(previousNavigationStartEvent: NavigationStart) {
        let currenturl: string;
        currenturl = this.currentRouteString.toString();
        if (!(previousNavigationStartEvent.url.indexOf('disclosure') > -1)) {
            localStorage.setItem('previousUrl', previousNavigationStartEvent.url); // not sure why we are storing the previous url
        }
        if (!(currenturl.indexOf('disclosure') > -1)) {
            localStorage.setItem('lastUrlBeforeDisc', currenturl); // storing the current one
        }
    }
    
    isValid(): boolean {
        return this.validSession;
    }

    invokeKeepAlive(): Observable<boolean> {
        if (this.isVendorSso) {
            this.invokeVendorKeepAlive();
        }
        let timeNow = Date.now();
        this.serviceUrl = this.serviceRouter.getServiceRouteURL(PortalComponentServiceDefinition.getInstance());
        return this.http.get(this.serviceUrl + '/session/keepalive?time=' + timeNow, { responseType: 'json' })
            .pipe(map(data => {
                this.logService.info(data);
                if (typeof data !== 'undefined' && data === 'Valid') {
                    this.validSession = true;
                    this.authService.setUserLoggedIn();
                    return this.validSession;
                } else {
                    this.validSession = false;
                    return this.validSession;
                }
            }))
            .pipe(
                retry(3),
                catchError(error => {
                    timeNow = Date.now();
                    if (error.status === 0) {
                        // A client-side or network error occurred. Handle it accordingly.
                        console.log('An error occurred:', error);
                        this.logService.info('session/keepalive client-site or network error occured:', error);
                    } else {
                        // The backend returned an unsuccessful response code.
                        // The response body may contain clues as to what went wrong.
                        console.log(`Backend returned code ${error.status}, body was: `, error);
                        this.logService.info(`Backend returned code ${error.status}, error occured:`, error);
                    }
                    return of(false);
                })
            );
    }
    /**
     * For eachlevel of route authGuard is configured
     * This triggers keepalive multiple times (once for each route level)
     * and causing performance issues
     * Modified to have async behavior only for validating session
     * while handling child routes
     */
    invokeAsyncKeepAlive(): Observable<boolean> {
        console.log('currentRouteString is ' + this.currentRouteString);
        if (this.ibd !== 'A51') {
            this.checkXfsForRoute(this.currentRouteString.toString());
        }
        let queryParams = this.currentRouteString.split('?')[1];
        if (!this.userLoggedIn) {
            this.isWhiteListUrl = true;
        }
        // Current route is only redirect to home page so adding to whitelist.
        if (this.currentRouteString === this.nxiUri) {
            this.isWhiteListUrl = true;
        }

        if (this.currentRouteString.toString().includes('redirectLandingPage')) {
            return of(this.checkAllowedToAccessRoutes());
        }
        if (queryParams && queryParams === 'landingPage=duplicatesessionconfirmation') {
            console.log('inside invokeAsyncKeepAlive duplicatesessionconfirmation--');
            this.checkAllowedToAccessRoutes();
            return of(true);
        } else  if (this.isWhiteListUrl) {
            console.log('currentRouteString is isWhiteListUrl' + this.currentRouteString);
            return of(true);
        } else {
            console.log('inside invokeAsyncKeepAlive ---');
            if (this.currentRouteString) {
                return this.invokeKeepAlive()
                    .pipe(map(data => {
                        this.logService.info(' User Session Valid ? ', data);
                        if (!data) {
                            this.logService.info('Session not valid load login page');
                            // remove the token added during login
                            // localStorage.removeItem('token');
                            this.validSession = false;
                            // location.href = '/' + 'nxi' + '/login';
                            this.logout();
                        }
                        this.checkAllowedToAccessRoutes();
                        console.log('after checkAllowedToAccessRoutes ---');
                        return data;
                    }));
            } else {
                return of(true);
            }
        }
    }

    rertiveVendorKeepAliveUrl(): void {
        let timeNow = Date.now();
        this.serviceUrl = this.serviceRouter.getServiceRouteURL(PortalComponentServiceDefinition.getInstance());
        this.http.get(this.serviceUrl + '/portfolio/keepAlive' + '?time=' + timeNow, { responseType: 'json' })
            .subscribe(data => {
                console.log(data);
                if (data) {
                    console.log('fetch vendor keepalive');
                    sessionStorage.setItem('vendorKeepAliveUrl', data.toString());
                    this.invokeKeepaliveUrl(data.toString());
                } else {
                    console.log('vendor keepalive not available');
                }
            }, err => {
                console.log('error while fetching keepalive' + err);
            });
    }

    invokeVendorKeepAlive(): void {
        let vendorUrl = sessionStorage.getItem('vendorKeepAliveUrl');
        console.log('vendorUrl ' + vendorUrl);
        if (vendorUrl) {
            this.invokeKeepaliveUrl(vendorUrl);
        } else {
            this.rertiveVendorKeepAliveUrl();
        }
        if (this.keepAlive) {
            this.invokeKeepaliveUrl(this.keepAlive);
        }
        if (this.keepAliveType === 'Imagetag') {
            let imgTag = document.createElement('img');
            imgTag.src = this.keepAlive;
            imgTag.style.display = 'none';
            imgTag.style.height = '0';
            imgTag.style.width = '0';
            imgTag.border = '0';
        }
    }

    invokeKeepaliveUrl(vendorUrl: string): void {
        if (!!vendorUrl && (vendorUrl !== 'null')) {
            if (this.cbolObj && this.cbolObj.enableCbolRestBranding ) {
                let cbolheaders = this.getCbolHeaders(this.cbolObj.reqheaders);
                this.http.post(vendorUrl , {}, { headers: cbolheaders , responseType: 'text' }).subscribe(data => {
                    this.logService.info('invoked cbol  keepalive Url');
                });
           } else {
                this.http.jsonp(vendorUrl, 'callback').subscribe(data => {
                   this.logService.info('invoke  vendor keepalive Url');
                }, err => {
                   console.log('error while fetching vendor keepalive' + err);
                });
           }
        } else {
            console.log('error while fetching vendor keepalive- url null or empty' );
        }
    }

    logout(message?: string): void {
        let timeNow = Date.now();
        this.logService.info('Invoking Logout');
        this.resetLocalStorage();
        this.serviceUrl = this.serviceRouter.getServiceRouteURL(PortalComponentServiceDefinition.getInstance());
        let params = new HttpParams();
        if (message) {
            params = params.append('message', message);
        } else {
            params = params.append('message', '');
        }
        this.http.get(this.serviceUrl + '/session/logout' + '?time=' + timeNow, { params, responseType: 'json' })
            .subscribe(data => {
                console.log('logout Url is ' + data);
                if (data && data.toString().includes('http')) {
                    location.href = data.toString();
                } else {
                    if (message) {
                        location.href = '/nxi/welcome?' + message + '=true&isMfe=true&isModern=true';
                    } else {
                        location.href = '/nxi/welcome?sessiontimedout=true&isMfe=true&isModern=true';
                    }
                }
            }, err => {
                location.href = '/nxi/welcome?sessiontimedout=true&isMfe=true&isModern=true';
            });
    }

    invokeDasfLogout(message?: string): void {
        this.logService.info('Invoking DASF Logout');
        this.resetLocalStorage();
        this.serviceUrl = this.serviceRouter.getServiceRouteURL(PortalComponentServiceDefinition.getInstance());
        let params = new HttpParams();
        if (message) {
            params = params.append('message', message);
        } else {
            params = params.append('message', '');
        }
        this.http.post( '/investordasfrest/api/session/logout', { params, responseType: 'json' })
            .subscribe(data => {
                this.logout(message);
            }, err => {
                location.href = '/nxi/welcome?sessiontimedout=true&isMfe=true&isModern=true';
            });
    }

    isUserSessionValid() {
        console.log('inside isUserSessionValid ---');
        // Work around to get both nxi login and dasf login app
        if (localStorage.getItem('cookie')) {
            return true;
        }
        return this.invokeKeepAlive()
            .pipe(map(data => {
                this.logService.info(' User Session Valid ? ', data);
                if (!data) {
                    this.logService.info('Session not valid load login page');
                    // remove the token added during login
                    // localStorage.removeItem('token');
                    this.validSession = false;
                    location.href = '/' + 'nxi' + '/login';
                }
                this.checkAllowedToAccessRoutes();
                return data;
            }));

    }

    resetLocalStorage() {
        this.validSession = false;
        if (localStorage.getItem('token')) {
            localStorage.removeItem('token');
        }
        sessionStorage.clear(); // remove everything from sessionstorage
        // for nxp login - will be removed
        if (localStorage.getItem('cookie')) {
            localStorage.removeItem('cookie');
        }
        sessionStorage.removeItem('nav_current_pageid');
        sessionStorage.removeItem('realTimeActive');
        if (sessionStorage.getItem('vendorKeepAliveUrl')) {
            sessionStorage.removeItem('vendorKeepAliveUrl');
        }
        if (this.storageService.getItem('nxiSelectedAccountValue')) {
            this.storageService.removeItem('nxiSelectedAccountValue');
        }
        if (sessionStorage.getItem('isThemeSelectionPopupShown')) {
            sessionStorage.removeItem('isThemeSelectionPopupShown');
        }
        if (sessionStorage.getItem('isGABannerClosedByUser')) {
            sessionStorage.removeItem('isGABannerClosedByUser');
        }
        this.resetExchangeAgmtSessionStorage();
        if (sessionStorage.getItem('rmvFinPlan')) {
            sessionStorage.removeItem('rmvFinPlan');
        }
    }
    private resetExchangeAgmtSessionStorage() {
        if (sessionStorage.getItem('NONPROFSELDATA')) {
            sessionStorage.removeItem('NONPROFSELDATA');
        }
        if (sessionStorage.getItem('NONPROFDATA')) {
            sessionStorage.removeItem('NONPROFDATA');
        }
        if (sessionStorage.getItem('NPSDDATA')) {
            sessionStorage.removeItem('NPSDDATA');
        }
        if (sessionStorage.getItem('NASDAQDATA')) {
            sessionStorage.removeItem('NASDAQDATA');
        }
    }
    isLoggedIn(): boolean {
        this.logService.info('returning isLoggedIn ', this.validSession);
        return this.validSession;
    }

    checkAllowedToAccessRoutes(): boolean {
        console.log('inside checkAllowedToAccessRoutes ---');
        let url = this.location.path(false);
        console.log('url checkAllowedToAccessRoutes ---' + url);
        let urlArray = url.split('/');
        console.log('urlArray checkAllowedToAccessRoutes ---' + urlArray);
        if (urlArray[urlArray.length - 1].split('?').length > 1) {
            let queryparam = urlArray[urlArray.length - 1].split('?')[1];
            this.landingRoute = queryparam.substring(12, urlArray[urlArray.length - 1].split('?')[1].length);
            urlArray[urlArray.length - 1] = urlArray[urlArray.length - 1].split('?')[0];
            console.log('queryparam checkAllowedToAccessRoutes ---' + queryparam + '-' + this.landingRoute);
        } else if (urlArray[urlArray.length - 1].split('#').length > 1) {
            urlArray[urlArray.length - 1] = urlArray[urlArray.length - 1].split('#')[0];
        } else if (urlArray[urlArray.length - 1].split(';').length > 1) {
            urlArray[urlArray.length - 1] = urlArray[urlArray.length - 1].split(';')[0];
        }
        if (urlArray[urlArray.length - 1] === 'redirectLandingPage') {
            console.log('redirectLandingPage checkAllowedToAccessRoutes ---');
            return this.redirectToRoute();
        } else {
            return this.checkEntitlementForRoute(urlArray[urlArray.length - 1], false);
        }
    }

    async getMenuConfig() {
        if (this.menuItems.length > 0) {
            if (this.configMenu.size > 0) {
                return new Promise((resolve) => {
                    resolve({ configMenu: this.configMenu });
                });
            } else {
                this.iterateMenu(this.menuItems, '/nxi', '');
                return new Promise((resolve) => {
                    resolve({ configMenu: this.configMenu });
                });
            }
        } else {
            return await this.navigationService.getMenuConfigurations().toPromise().then((data: any[]) => {
                this.menuItems = data;
                this.iterateMenu(this.menuItems, '/nxi', '');
                return { configMenu: this.configMenu };
            });
        }
    }

    /*getMenuConfig(): Observable<any> {
        // .subscribe((data: any[]) => {
        return this.http.get('src/assets/menu-config.json')
            .pipe(
                map((data: any[]) => {
                    this.menuItems = data;
                    // this.menuConfig$.next();
                    this.iterateMenu(this.menuItems, '/nxi', '');
                    return this.menuConfigObservable$;
                }
                    , err => {
                        console.log(' Error while fetching menu config', err);
                        this.menuConfig$.next([]);
                        return this.menuConfigObservable$;
                    }));
        // return this.menuConfigObservable$;
    }*/
    private isLandingPageValid(value: string): boolean {
        if (value === 'default' || value === 'null' || value === null || value === 'undefined' || value === undefined) {
            return false;
        }
        return true;
    }

    private getFirstAvailableLandingPage(menuItems: any[]): string {
        if (menuItems && Array.isArray(menuItems)) {
            if (menuItems[0] && menuItems[0].children && menuItems[0].children.length > 0 && menuItems[0].children[0].id) {
                return menuItems[0].children[0].id;
            }
        }
        return 'null';
    }

    private checkOverviewExist(menuItems: any[]): boolean {
        if (menuItems && Array.isArray(menuItems)) {
            const menu = menuItems.find((menuItem) =>
                menuItem && menuItem.id === 'overview' || menuItem && menuItem.children && menuItem.children.find((childMenuItem: any) => childMenuItem && childMenuItem.id === 'overview')
            );
            if (menu) {
                return true;
            }
        }
        return false;
    }

    private redirectToRoute(): boolean {
        let queryParam = new URLSearchParams(location.search);
        this.qpGlobal = new URLSearchParams(location.search);
        this.landingRoute = queryParam.get('landingPage') || this.landingRoute;
        if (!!this.landingRoute) {
            // So log in message would get fetched once only.
            localStorage.setItem('lp', 'true');
        }
        if (this.currentRouteString.includes('/nxi/redirectLandingPage')) {
            if (((queryParam && queryParam.get('landingPage') === 'default') || (queryParam.get('landingPage') === undefined)) && this.menuItems[0]) {
                if (this.checkOverviewExist(this.menuItems)) {
                    this.landingRoute = 'overview';
                } else {
                    this.landingRoute = this.getFirstAvailableLandingPage(this.menuItems);
                }
            }
        }


        queryParam.delete('landingPage');
        if (!this.isLandingPageValid(this.landingRoute)) {
            return this.iterateMenu(this.menuItems, '/nxi', 'default');
        } else if (PortalConstants.LandingPageMap[this.landingRoute]) {
            this.router.navigate([PortalConstants.LandingPageMap[this.landingRoute]], { queryParams: Object.fromEntries(queryParam) });
            return true;
        } else {
            console.log('checkEntitlementForRoute ---');
            if (queryParam) {
                queryParam.delete('landingPage');
                queryParam.delete('communityName');
                queryParam.delete('userThemeType');
                let param = PershingHttpUtil.getQueryStringFromJsonObject(Object.fromEntries(queryParam));

                this.contextService.setRouteContext(param);
            }
            if (this.landingRoute === 'default-settings' && this.deviceType === 'mobile') {
                this.landingRoute = 'settings';
            }
            return this.checkEntitlementForRoute(this.landingRoute, true);
        }
    }

    private checkRedirectLandingPage(queryParam: any) {
        if (this.currentRouteString.includes('/nxi/redirectLandingPage')) {
            if (((queryParam && queryParam.landingPage === 'default') || (queryParam.landingPage === undefined)) && this.menuItems[0]) {
                if (this.checkOverviewExist(this.menuItems)) {
                    this.landingRoute = 'overview';
                } else {
                    this.landingRoute = this.getFirstAvailableLandingPage(this.menuItems);
                }
            }
        }
    }

    private iterateMenu(menu: any, parent: any, landingPage: any): boolean {
        for (let i = 0; i < menu.length; i++) {
            if (this.authorizationService.isEntitled(menu[i].entitlements)) {
                const isBreak = this.updateMenuUrl(parent, menu, i, landingPage);
                if (isBreak) {
                    return true;
                }
            }
        }
        return false;
    }

    private updateMenuUrl(parent: any, menu: any, i: number, landingPage: string) {
        menu[i].url = parent + '/' + menu[i].id;
        if (landingPage !== 'default') {
            this.configMenu.set(menu[i].id, { 'entitlements': menu[i].entitlements, 'url': menu[i].url });
        }
        if (menu[i].children && menu[i].children.length > 0) {
            let isBreak = this.iterateMenu(menu[i].children, menu[i].url, landingPage);
            if (isBreak) {
                return true;
            }
        } else if (landingPage === 'default') {
            this.isLandingRedirect = true;
            // For edocs though the first iterable page is all communication, we need to land on settings.
            if (menu[i].url.includes('communications')) {
              menu[i].url = '/nxi/settings/allsettings';
            }
            this.navigationService.setContextAndNavigate('', null, menu[i].url);
            return true;
        }

        return false;
    }

    private checkEntitlementForRoute(route: any, isRedirect: any): boolean {
        console.log('In checkEntitlementForRoute' + route + '--' + isRedirect);
        let mapValue = this.configMenu.get(route);
        if (isRedirect && mapValue && this.authorizationService.isEntitled(mapValue.entitlements)) {
            console.log('mapvalue if--' + mapValue.entitlements + '' + mapValue.url);
            this.navigationService.setContextAndNavigate('', null, mapValue.url);
            return true;
        } else if (mapValue && this.authorizationService.isEntitled(mapValue.entitlements)) {
            console.log('check else if');
            return true;
        }
        console.log('return false');
        return false;
    }

    private checkXfsForRoute(currRoute: string) {

        // should not proceed any further if no framing detected or already previously checked.
        let framedByDomainKey = 'fBDn' + document.domain;
        let framedByDomain = sessionStorage.getItem(framedByDomainKey);
        if ((top && top.location === location) || !!framedByDomain) {
            return;
        }

        let pageUri = currRoute.substring(currRoute.lastIndexOf('/'));
        if (pageUri.indexOf('?') >= 0) {
            pageUri = pageUri.substring(0, pageUri.indexOf('?'));
        } else if (pageUri.indexOf('#') >= 0) {
            pageUri = pageUri.substring(0, pageUri.indexOf('#'));
        }
        if (this.XFSTargetUrlList.indexOf(pageUri) && !this.isWhiteList) {
            switch (this.xfsOption) {
                case 'XFS_LogTrace':
                    this._xfsLogging();
                    this._setAllowedFrame();
                    break;
                case 'XFS_FrameBreak':
                    this._xfsFramebreak();
                    break;
                case 'XFS_LogNFrameBreak':
                    this._xfsLogging();
                    this._xfsFramebreak();
                    break;
                default:
                    return;
            }
        }
    }

    private _xfsLogging() {
        let timeNow = Date.now();
        this.serviceUrl = this.serviceRouter.getServiceRouteURL(PortalComponentServiceDefinition.getInstance());
        this.http.get(this.serviceUrl + '/portfolio/xfslogging' + '?time=' + timeNow, { responseType: 'json' }).subscribe(
            data => { },
            err => {
                console.log('Error with logging xfs.');
            });
        return;
    }

    private _xfsFramebreak() {
      if(top) {
        top.location.href = location.href;
      }
    }

    private _setAllowedFrame() {
        let framedByDomain = escape(document.referrer);
        let framedByDomainKey = 'fBDn' + document.domain;
        sessionStorage.setItem(framedByDomainKey, framedByDomain);
    }

    public redirectToNxiLandingPage(userId: string, ibd: string, lastLogin: any, dfp: string): Observable<any> {
        let body = new HttpParams();
        body = body.set('userId', userId);
        body = body.set('ibd', ibd);
        body = body.set('lastLogin', lastLogin);
        body = body.set('dfp', dfp);
        return this.http.post(this.serviceUrl + '/landingPage/getLandingPageforDasf', body, { responseType: 'json' });
    }
    public getBrandedRedirect(userId: string, ibd: string, userSourceCode: string, digitalFingerprint: string,dbReturnCode: string): Observable<any> {
        let body = new HttpParams();
        body = body.set('userId', userId);
        body = body.set('ibd', ibd);
        body = body.set('password', '');
        body = body.set('rememberMe', '');
        body = body.set('dasfResponse', '');
        body = body.set('digitalFingerprint', digitalFingerprint);
        body = body.set('digitalFingerprintHash', '');
        if (userSourceCode && userSourceCode !== '') {
            body = body.set('userSourceCode', userSourceCode);
        }
        if (dbReturnCode && dbReturnCode !== '') {
            body = body.set('dbReturnCode', dbReturnCode);
        }
        return this.http.post(this.serviceUrl + '/prelogin/brandedRedirect', body, { responseType: 'json' });
    }

    public isOneClickEdelEnabled(): Observable<any> {
        let timeNow = Date.now();
        return this.http.get(this.serviceUrl + '/portfolio/show-oneclick-edel' + '?time=' + timeNow, { responseType: 'json' });
    }

    public fetchCbolBranding(cbolparams: any): Observable<any> {
        let timeNow = Date.now();
        let cbolheaders = this.getCbolHeaders(cbolparams.reqheaders);
        return this.http.get(cbolparams.url , { headers: cbolheaders , responseType: 'text' });
    }

    getCbolHeaders(reqheaders: string): HttpHeaders {
        const headersArray = reqheaders ?  (reqheaders + '').split('||') : '';
        let header = {};
        for (let hdr of headersArray) {
            if (hdr && hdr.includes('=')) {
                const idx = hdr.indexOf('=') ;
                header[hdr.substring(0, idx)] = hdr.substring(idx + 1);
            }
        }
        return new HttpHeaders(header);
    }
    public showSiteTermsAndConditions(userId: string, ibd: string): Observable<any> {
        let body = new HttpParams();
        body = body.set('userId', userId);
        body = body.set('ibd', ibd);
        return this.http.post(this.serviceUrl + '/prelogin/siteTermsAndConditions', body, { responseType: 'json' });
    }
}
